import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Plus, Search, Building2 } from "lucide-react";
import { toast } from "sonner";
import { brandify } from "../lib/brand";

const SERVICE_BADGE = {
  "StaffHub Direct": "bg-sky-50 text-sky-700 border-sky-200",
  "StaffHub Managed": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "StaffHub AI": "bg-purple-50 text-purple-700 border-purple-200",
  "StaffHub Hybrid": "bg-orange-50 text-orange-700 border-orange-200",
  "StaffHub Teams": "bg-indigo-50 text-indigo-700 border-indigo-200",
};
const STATUS_TONE = {
  "Active Client":"bg-emerald-50 text-emerald-700 border-emerald-200",
  "Prospect":"bg-slate-100 text-slate-700 border-slate-200",
  "Discovery":"bg-sky-50 text-sky-700 border-sky-200",
  "Proposal":"bg-amber-50 text-amber-700 border-amber-200",
  "Inactive":"bg-slate-100 text-slate-500 border-slate-200",
  "Lost":"bg-red-50 text-red-700 border-red-200",
};

export default function Companies() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name:"", industry:"", city:"", state:"", country:"USA", primary_contact_name:"", primary_contact_email:"", status:"Prospect", recommended_solution:"StaffHub Direct" });

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (status) params.status = status;
    const { data } = await api.get("/companies", { params });
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, status]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/companies", form);
      toast.success("Company created");
      setOpen(false); setForm({ name:"", industry:"", city:"", state:"", country:"USA", primary_contact_name:"", primary_contact_email:"", status:"Prospect", recommended_solution:"StaffHub Direct" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Layout>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">Companies & CRM</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">Client accounts</h1>
          <p className="text-sm text-slate-500 mt-1">Prospects, active clients, and workforce opportunity scoring.</p>
        </div>
        <button data-testid="new-company-btn" onClick={()=>setOpen(true)} className="h-10 px-4 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold inline-flex items-center gap-2"><Plus size={15}/> New company</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50/50 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input data-testid="companies-search" value={q} onChange={e=>setQ(e.target.value)} placeholder="Search companies…" className="w-full pl-9 pr-3 h-9 rounded-md border border-slate-200 text-sm focus:border-sky-500 outline-none bg-white"/>
          </div>
          <select data-testid="companies-status-filter" value={status} onChange={e=>setStatus(e.target.value)} className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white">
            <option value="">All statuses</option>
            {Object.keys(STATUS_TONE).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="text-xs text-slate-500 ml-auto">{rows.length} results</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100/70 text-slate-600 text-[11px] uppercase tracking-widest font-semibold">
              <tr>
                <th className="text-left px-4 py-3">Company</th>
                <th className="text-left px-4 py-3">Industry</th>
                <th className="text-left px-4 py-3">Location</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Recommended solution</th>
                <th className="text-right px-4 py-3">Opp. score</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} data-testid={`company-row-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-md bg-slate-100 text-slate-500 flex items-center justify-center"><Building2 size={14}/></div>
                      <div>
                        <div className="font-semibold text-slate-900">{r.name} {r.is_demo && <span className="ml-1 text-[9px] uppercase tracking-widest text-slate-400 border border-slate-200 rounded px-1">DEMO</span>}</div>
                        <div className="text-[11px] text-slate-500">{r.primary_contact_name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{r.industry || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{[r.city, r.state, r.country].filter(Boolean).join(", ")}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex text-[10px] font-semibold px-2 py-0.5 rounded border ${STATUS_TONE[r.status] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    {r.recommended_solution && <span className={`inline-flex text-[10px] font-semibold px-2 py-0.5 rounded border ${SERVICE_BADGE[r.recommended_solution] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{r.recommended_solution}</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900 font-semibold">{Math.round(((r.ai_opportunity_score||0)+(r.global_talent_score||0)+(r.remote_suitability||0))/3)}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-sm text-slate-500">No companies yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-6" onClick={()=>setOpen(false)}>
          <form onClick={e=>e.stopPropagation()} onSubmit={submit} data-testid="new-company-form" className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="font-display font-bold text-xl text-slate-900 mb-4">New company</h2>
            <div className="grid grid-cols-2 gap-4">
              {[
                ["name","Company name*",true],["industry","Industry"],["city","City"],["state","State"],
                ["country","Country"],["primary_contact_name","Primary contact"],["primary_contact_email","Contact email"],["primary_contact_phone","Contact phone"],
              ].map(([k,l,req]) => (
                <div key={k}>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">{l}</label>
                  <input data-testid={`company-field-${k}`} required={req} value={form[k]||""} onChange={e=>setForm({...form,[k]:e.target.value})} className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm outline-none focus:border-sky-500"/>
                </div>
              ))}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Status</label>
                <select value={form.status} onChange={e=>setForm({...form,status:e.target.value})} className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm">
                  {Object.keys(STATUS_TONE).map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Recommended solution</label>
                <select value={form.recommended_solution} onChange={e=>setForm({...form,recommended_solution:e.target.value})} className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm">
                  {Object.keys(SERVICE_BADGE).map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={()=>setOpen(false)} className="h-9 px-4 rounded-md border border-slate-300 text-sm">Cancel</button>
              <button data-testid="company-submit" type="submit" className="h-9 px-4 rounded-md bg-slate-900 text-white text-sm font-semibold">Create</button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
